import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Badge,
  Container,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Select,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import MessageIcon from "@mui/icons-material/Message";
import SettingsIcon from "@mui/icons-material/Settings";
import SendIcon from "@mui/icons-material/Send";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useAuthentication } from "../../store/AppContext";
import { DESIGN_TOKENS } from "../../styles/theme";
import { pl, useI18n } from "../../i18n";
import { resolveAssetUrl } from "../../utils";

// ============================================================
// NAVIGATION ITEMS
// ============================================================

interface NavigationItem {
  labelKey: string;
  path: string;
}

interface AccountNavigationItem {
  icon: React.ReactNode;
  label: string;
  path: string;
}

const MAIN_NAVIGATION_ITEMS: NavigationItem[] = [
  { labelKey: "nav.home", path: "/" },
  { labelKey: "nav.listings", path: "/listings" },
  { labelKey: "nav.companiesCatalog", path: "/companies" },
  { labelKey: "nav.faq", path: "/faq" },
  { labelKey: "nav.contact", path: "/contact" },
];

const getAccountNavigationItems = (userType?: string): AccountNavigationItem[] => {
  const commonItems = [
    { label: pl.account.dashboard, path: "/account", icon: <DashboardIcon fontSize="small" /> },
    { label: pl.account.favourites, path: "/account/favourites", icon: <BookmarkIcon fontSize="small" /> },
  ];

  const pilotItems = [
    { label: pl.account.myListings, path: "/account/my-listings", icon: <ListAltIcon fontSize="small" /> },
    { label: pl.account.sentApplications, path: "/account/sent-applications", icon: <SendIcon fontSize="small" /> },
    { label: pl.account.myOrders, path: "/account/my-orders", icon: <ListAltIcon fontSize="small" /> },
    { label: pl.account.preferences, path: "/account/preferences", icon: <LocationOnIcon fontSize="small" /> },
  ];

  const companyItems = [
    { label: pl.account.listingsAndOffers, path: "/account/listings-and-offers", icon: <ListAltIcon fontSize="small" /> },
  ];

  const bottomItems = [
    { label: pl.account.messages, path: "/account/messages", icon: <MessageIcon fontSize="small" /> },
    { label: pl.account.settings, path: "/account/settings", icon: <SettingsIcon fontSize="small" /> },
  ];

  if (userType === "PILOT_B2B_COMPANY") {
    return [...commonItems, ...pilotItems, ...bottomItems];
  }

  if (userType === "TRANSPORT_COMPANY") {
    return [...commonItems, ...companyItems, ...bottomItems];
  }

  return [...commonItems, ...bottomItems];
};

// ============================================================
// LOGO COMPONENT
// ============================================================

function AppLogo() {
  return (
    <Box
      component={RouterLink}
      to="/"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        textDecoration: "none",
        "&:hover": { textDecoration: "none" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src="/Logo.jpeg"
          alt="QPRO Connect"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
          fontWeight: 700,
          fontSize: "1.25rem",
          color: DESIGN_TOKENS.colors.textPrimary,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        QPRO<span style={{ color: DESIGN_TOKENS.colors.primary }}>Connect</span>
      </Typography>
    </Box>
  );
}

// ============================================================
// NAVIGATION BAR COMPONENT
// ============================================================

interface NavigationBarProps {
  children?: React.ReactNode;
}

export function NavigationBar({ children }: NavigationBarProps) {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { isAuthenticated, currentUser, logoutUser } = useAuthentication();

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [notificationsMenuAnchorElement, setNotificationsMenuAnchorElement] =
    useState<null | HTMLElement>(null);
  const [accountMenuAnchorElement, setAccountMenuAnchorElement] =
    useState<null | HTMLElement>(null);

  const handleOpenNotificationsMenu = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setNotificationsMenuAnchorElement(event.currentTarget);
  };

  const handleCloseNotificationsMenu = () => {
    setNotificationsMenuAnchorElement(null);
  };

  const handleOpenAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchorElement(event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAccountMenuAnchorElement(null);
  };

  const handleLogout = () => {
    logoutUser();
    handleCloseAccountMenu();
  };

  const userLogoUrl = resolveAssetUrl(
    currentUser?.membership?.company_transport?.company_information?.brand_graphics?.logo?.url ??
      currentUser?.membership?.pilot?.company_information?.brand_graphics?.logo?.url,
  );
  const accountNavigationItems = getAccountNavigationItems(
    currentUser?.membership?.USER_TYPE,
  );

  const isCurrentRoute = (path: string): boolean => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 0 }, py: 1, minHeight: "68px !important" }}>
            <AppLogo />

            {/* Desktop Navigation */}
            {!isMobileView && (
              <Box
                component="nav"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 4 }}
              >
                {MAIN_NAVIGATION_ITEMS.map((navItem) => (
                  <Button
                    key={navItem.path}
                    component={RouterLink}
                    to={navItem.path}
                    sx={{
                      color: isCurrentRoute(navItem.path)
                        ? DESIGN_TOKENS.colors.primary
                        : DESIGN_TOKENS.colors.textSecondary,
                      fontWeight: isCurrentRoute(navItem.path) ? 600 : 500,
                      fontSize: "0.9rem",
                      px: 1.5,
                      position: "relative",
                      "&::after": isCurrentRoute(navItem.path)
                        ? {
                            content: '""',
                            position: "absolute",
                            bottom: 4,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "60%",
                            height: "2px",
                            backgroundColor: DESIGN_TOKENS.colors.primary,
                            borderRadius: "1px",
                          }
                        : {},
                      "&:hover": {
                        color: DESIGN_TOKENS.colors.primary,
                        backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                      },
                    }}
                  >
                    {t(navItem.labelKey)}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Account Area */}
            {!isMobileView && (
              <>
                {isAuthenticated ? (
                  <>
                    <Button
                      onClick={handleOpenNotificationsMenu}
                      aria-label="Powiadomienia"
                      variant="contained"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        textTransform: "none",
                        borderRadius: "999px",
                        px: 1.5,
                        py: 0.75,
                        minWidth: "auto",
                        boxShadow: "none",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                        }}
                      >
                        <Badge badgeContent={0} color="error">
                          <NotificationsNoneIcon sx={{ color: "#fff" }} />
                        </Badge>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#fff" }}
                      >
                        Powiadomienia
                      </Typography>
                    </Button>
                    <Menu
                      anchorEl={notificationsMenuAnchorElement}
                      open={Boolean(notificationsMenuAnchorElement)}
                      onClose={handleCloseNotificationsMenu}
                      transformOrigin={{ horizontal: "right", vertical: "top" }}
                      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                      PaperProps={{
                        elevation: 2,
                        sx: { mt: 1, minWidth: 260 },
                      }}
                    >
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Powiadomienia
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Brak powiadomień.
                        </Typography>
                      </Box>
                    </Menu>
                    <Button
                      onClick={handleOpenAccountMenu}
                      variant="contained"
                      sx={{
                        ml: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        textTransform: "none",
                        borderRadius: "999px",
                        px: 1,
                        py: 0.5,
                        minWidth: "auto",
                        boxShadow: "none",
                      }}
                      aria-label={t("nav.accountMenuAria")}
                    >
                      <Avatar
                        src={userLogoUrl}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: userLogoUrl ? DESIGN_TOKENS.colors.surface : DESIGN_TOKENS.colors.primary,
                          fontSize: "0.875rem",
                          fontWeight: 700,
                          border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                          "& .MuiAvatar-img": {
                            objectFit: "contain",
                            padding: userLogoUrl ? "4px" : 0,
                          },
                        }}
                      >
                        {currentUser?.username?.[0]?.toUpperCase() ?? "U"}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#fff" }}
                      >
                        {t("nav.myAccount")}
                      </Typography>
                    </Button>
                    <Menu
                      anchorEl={accountMenuAnchorElement}
                      open={Boolean(accountMenuAnchorElement)}
                      onClose={handleCloseAccountMenu}
                      transformOrigin={{ horizontal: "right", vertical: "top" }}
                      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                      PaperProps={{
                        elevation: 2,
                        sx: { mt: 1, minWidth: 200 },
                      }}
                    >
                      {accountNavigationItems.map((navItem) => (
                        <MenuItem
                          key={navItem.path}
                          component={RouterLink}
                          to={navItem.path}
                          onClick={handleCloseAccountMenu}
                        >
                          {navItem.icon}
                          <Box component="span" sx={{ ml: 1.25 }}>
                            {navItem.label}
                          </Box>
                        </MenuItem>
                      ))}
                      <Divider />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{ color: "error.main" }}
                      >
                        {t("nav.logout")}
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      component={RouterLink}
                      to="/login"
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      {t("nav.login")}
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/register"
                      variant="contained"
                      color="primary"
                      size="small"
                    >
                      {t("nav.register")}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Mobile Hamburger */}
            {isMobileView && (
              <IconButton
                onClick={() => setIsMobileDrawerOpen(true)}
                aria-label={t("nav.openMenuAria")}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          }}
        >
          <AppLogo />
          <IconButton onClick={() => setIsMobileDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 1 }}>
          {MAIN_NAVIGATION_ITEMS.map((navItem) => (
            <ListItem key={navItem.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={navItem.path}
                onClick={() => setIsMobileDrawerOpen(false)}
                selected={isCurrentRoute(navItem.path)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                    color: DESIGN_TOKENS.colors.primary,
                  },
                }}
              >
                <ListItemText
                  primary={t(navItem.labelKey)}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <Divider sx={{ my: 1 }} />
          {isAuthenticated ? (
            <>
              {accountNavigationItems.map((navItem) => (
                <ListItem key={navItem.path} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={navItem.path}
                    onClick={() => setIsMobileDrawerOpen(false)}
                  >
                    <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
                      {navItem.icon}
                    </Box>
                    <ListItemText primary={navItem.label} />
                  </ListItemButton>
                </ListItem>
              ))}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleLogout();
                    setIsMobileDrawerOpen(false);
                  }}
                  sx={{ color: "error.main" }}
                >
                  <ListItemText primary={t("nav.logout")} />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/login"
                  onClick={() => setIsMobileDrawerOpen(false)}
                >
                  <ListItemText primary={t("nav.login")} />
                </ListItemButton>
              </ListItem>
              <ListItem sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  fullWidth
                  onClick={() => setIsMobileDrawerOpen(false)}
                >
                  {t("nav.register")}
                </Button>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>

      {/* Page Content */}
      <Box component="main" sx={{ minHeight: "calc(100vh - 68px)" }}>
        {children}
      </Box>

      {/* Footer */}
      <AppFooter />
    </>
  );
}

// ============================================================
// FOOTER COMPONENT
// ============================================================

function AppFooter() {
  const { language, setLanguage, t } = useI18n();

  return (
    <Box
      component="footer"
      sx={{
        borderTop: `1px solid ${DESIGN_TOKENS.colors.border}`,
        backgroundColor: DESIGN_TOKENS.colors.surface,
        py: 4,
        mt: 8,
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 3,
          }}
        >
          <Box>
            <AppLogo />
            <Typography variant="body2" sx={{ mt: 1.5, maxWidth: 320 }}>
              {t("nav.footerDescription")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: { xs: 1, sm: 4 },
            }}
          >
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  mb: 1,
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: DESIGN_TOKENS.colors.textSecondary,
                }}
              >
                {t("nav.platform")}
              </Typography>
              {MAIN_NAVIGATION_ITEMS.map((navItem) => (
                <Box key={navItem.path} sx={{ mb: 0.5 }}>
                  <Typography
                    component={RouterLink}
                    to={navItem.path}
                    variant="body2"
                    sx={{
                      color: DESIGN_TOKENS.colors.textSecondary,
                      "&:hover": { color: DESIGN_TOKENS.colors.primary },
                    }}
                  >
                    {t(navItem.labelKey)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  mb: 1,
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: DESIGN_TOKENS.colors.textSecondary,
                }}
              >
                {t("nav.legal")}
              </Typography>
              <Box sx={{ mb: 0.5 }}>
                <Typography
                  component={RouterLink}
                  to="/terms-of-service"
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.textSecondary,
                    "&:hover": { color: DESIGN_TOKENS.colors.primary },
                  }}
                >
                  {t("nav.terms")}
                </Typography>
              </Box>
              <Box>
                <Typography
                  component={RouterLink}
                  to="/privacy-policy"
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.textSecondary,
                    "&:hover": { color: DESIGN_TOKENS.colors.primary },
                  }}
                >
                  {t("nav.privacy")}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  mb: 1,
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: DESIGN_TOKENS.colors.textSecondary,
                }}
              >
                {t("nav.language")}
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel id="footer-language-select-label">
                  {t("nav.language")}
                </InputLabel>
                <Select
                  labelId="footer-language-select-label"
                  value={language}
                  label={t("nav.language")}
                  onChange={(event) =>
                    setLanguage(event.target.value as "pl" | "en")
                  }
                >
                  <MenuItem value="pl">{t("nav.polish")}</MenuItem>
                  <MenuItem value="en">{t("nav.english")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          © {new Date().getFullYear()} {t("app.copyright")}
        </Typography>
      </Container>
    </Box>
  );
}
